import type { DayType, MealSlot } from '@cadencia/contracts';
import { requireFood } from '@cadencia/domain';
import type { ReactNode } from 'react';
import './nutricion.css';

/**
 * Los huecos del dia, en castellano.
 *
 * FUENTE UNICA de estos nombres: la pantalla de configuracion, el dia de
 * nutricion, el detalle de comida y la lista de la compra pintan todos desde
 * aqui. Un `slot === 'breakfast' ? 'Desayuno' : …` suelto en una pantalla es
 * el que se queda sin actualizar cuando se añade una franja, y asi es como
 * `afternoon_snack` habria salido en pantalla como «afternoon_snack».
 *
 * `extra` decia «Merienda» hasta el 08/09/2026, cuando la merienda paso a ser
 * una franja de verdad. Ahora dice lo que es: un hueco del esquema anterior que
 * ya no se genera y que solo aparece en comidas antiguas.
 */
export const NOMBRE_DE_SLOT: Record<MealSlot, string> = {
  breakfast: 'Desayuno',
  midmorning: 'Media mañana',
  lunch: 'Almuerzo',
  afternoon_snack: 'Merienda',
  dinner: 'Cena',
  extra: 'Comida extra',
};

/**
 * El tipo de dia en palabras. `dayType` es un valor persistido en ingles
 * (`nutrition_targets.day_type`) y no puede salir tal cual a la pantalla:
 * hasta el 28/09/2026 se pintaba «un dia strength».
 */
export const NOMBRE_DE_TIPO_DE_DIA: Record<DayType, string> = {
  strength_high: 'de fuerza con pierna',
  strength: 'de fuerza',
  cardio: 'de cardio',
  rest: 'de descanso',
};

/**
 * Un macro frente a su objetivo: cifra, etiqueta y barra.
 *
 * Vive aqui porque lo pintan DOS pantallas —el dia de nutricion y la tarjeta
 * «Tu objetivo de hoy» de Inicio (§4.1)— y son el mismo dato. Duplicarlo
 * dejaria dos formas de leer lo mismo que se separarian al primer retoque.
 *
 * La barra se corta en el 100 %: pasarse del objetivo es informacion, pero una
 * barra que se sale de su caja no la comunica, la rompe.
 *
 * TRES LINEAS Y NO DOS (08/09/2026). La etiqueta y el objetivo iban juntos en
 * el mismo parrafo —«proteina / 162 g»— dentro de una clase con
 * `text-transform: uppercase`, asi que en pantalla salia «PROTEINA / 162 G»:
 * la unidad en mayuscula, que en el sistema internacional significa otra cosa,
 * y una linea que en 393 px se partia en dos. Ahora son tres papeles distintos
 * en tres estilos distintos: QUE es (versalita), CUANTO llevas (la cifra) y
 * CONTRA QUE (el objetivo, en minuscula y en tinta secundaria).
 */
export function Macro({
  etiqueta,
  actual,
  objetivo,
  unidad,
}: {
  etiqueta: string;
  actual: number;
  objetivo: number | undefined;
  unidad: string;
}): ReactNode {
  const porcentaje =
    objetivo === undefined || objetivo <= 0 ? 0 : Math.min(100, (actual / objetivo) * 100);
  return (
    <div className="macro">
      <p className="macro__etiqueta">{etiqueta}</p>
      <p className="macro__cifra">
        <span className="macro__valor numeros">{Math.round(actual)}</span>
        {objetivo !== undefined && (
          <span className="macro__objetivo numeros">
            /{Math.round(objetivo)} {unidad}
          </span>
        )}
      </p>
      <div className="macro__barra" aria-hidden="true">
        <span style={{ width: `${porcentaje}%` }} />
      </div>
    </div>
  );
}

/**
 * Indicador persistente de «gramos en crudo» (CA-4.7, §5.6).
 *
 * Pegado arriba y sin boton de cerrar. La diferencia entre 100 g de arroz y
 * 100 g de arroz cocido es de casi el triple de energia: una convencion que se
 * comunica una sola vez, en el onboarding, es una convencion que se olvida
 * justo cuando se esta pesando.
 */
export function AvisoCrudo(): ReactNode {
  return (
    <p className="aviso-crudo" role="note">
      <strong>Gramos en crudo.</strong> Salvo que el alimento diga lo contrario, las cantidades
      son del producto sin cocinar.
    </p>
  );
}

/**
 * Nota de un alimento cuyo valor se refiere al producto ya elaborado.
 *
 * La excepcion a la regla de arriba tiene que verse EN EL ALIMENTO, no en una
 * nota general: si el aviso de crudo estuviera solo y hubiera excepciones sin
 * marcar, el aviso seria falso la mitad de las veces.
 */
export function NotaDeAlimento({ foodSlug }: { foodSlug: string }): ReactNode {
  const food = requireFood(foodSlug);
  if (!food.isCookedReference || food.referenceNote === null) return null;
  return <span className="ingrediente__nota">{food.referenceNote}</span>;
}
